import React, { useRef, useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Eye, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface BugDropzoneProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxSizeMb?: number;
}

export const BugDropzone: React.FC<BugDropzoneProps> = ({
  files,
  onFilesChange,
  maxFiles = 5,
  maxSizeMb = 10,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [activePreview, setActivePreview] = useState<{ url: string; name: string } | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Generate object URLs for file previews
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviewUrls(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  const addValidFiles = (incomingFiles: File[]) => {
    setValidationError(null);

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const valid: File[] = [];

    for (const f of incomingFiles) {
      if (!allowedTypes.includes(f.type) && !f.type.startsWith('image/')) {
        setValidationError(`"${f.name}" is not a supported image format. Please use PNG, JPEG, or WebP.`);
        continue;
      }
      if (f.size > maxSizeMb * 1024 * 1024) {
        setValidationError(`"${f.name}" exceeds the ${maxSizeMb}MB maximum file size limit.`);
        continue;
      }
      valid.push(f);
    }

    if (files.length + valid.length > maxFiles) {
      setValidationError(`You can attach a maximum of ${maxFiles} screenshots per report.`);
      const remainingSlots = Math.max(0, maxFiles - files.length);
      const trimmed = valid.slice(0, remainingSlots);
      if (trimmed.length > 0) {
        onFilesChange([...files, ...trimmed]);
      }
      return;
    }

    if (valid.length > 0) {
      onFilesChange([...files, ...valid]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addValidFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addValidFiles(Array.from(e.target.files));
      e.target.value = ''; // Reset
    }
  };

  const removeFile = (indexToRemove: number) => {
    setValidationError(null);
    onFilesChange(files.filter((_, idx) => idx !== indexToRemove));
  };

  return (
    <div className="space-y-3">
      {/* Drop / Browse / Paste Area */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-4 sm:p-5 text-center cursor-pointer transition-all duration-200 ${
          isDragOver
            ? 'border-emerald-600 bg-emerald-50/70 scale-[1.01]'
            : 'border-slate-300 hover:border-slate-400 bg-slate-50/70 hover:bg-slate-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
        />
        <div className="flex flex-col items-center justify-center gap-1.5">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 mb-1">
            <Upload className="w-5 h-5 text-emerald-700" />
          </div>
          <p className="text-xs sm:text-sm font-semibold text-slate-800">
            Paste screenshot (<kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-slate-200 rounded border border-slate-300 font-bold">Ctrl+V</kbd>), drag & drop, or browse
          </p>
          <p className="text-[11px] text-slate-500">
            PNG, JPEG, WebP • Max {maxSizeMb}MB each • Up to {maxFiles} images
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-1.5 text-xs font-semibold h-7 border-slate-300 text-slate-700"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Choose from Files / Photos
          </Button>
        </div>
      </div>

      {/* Validation Error Banner */}
      {validationError && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
          <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
          <span>{validationError}</span>
        </div>
      )}

      {/* Attachment Previews Grid */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-700">
            <span>Attached Screenshots ({files.length}/{maxFiles})</span>
            <span className="text-[11px] text-slate-500">
              Total: {(files.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)).toFixed(2)} MB
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {files.map((file, idx) => {
              const url = previewUrls[idx];
              return (
                <div
                  key={`${file.name}_${idx}`}
                  className="group relative border border-slate-200 bg-white rounded-lg p-1.5 shadow-xs flex flex-col overflow-hidden hover:border-emerald-300 transition-colors"
                >
                  {/* Image Thumbnail */}
                  <div
                    onClick={() => setActivePreview({ url, name: file.name })}
                    className="relative w-full h-24 sm:h-28 rounded bg-slate-100 overflow-hidden cursor-zoom-in flex items-center justify-center"
                  >
                    {url ? (
                      <img
                        src={url}
                        alt={file.name}
                        className="w-full h-full object-cover object-center"
                      />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-slate-400" />
                    )}
                    <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/20 transition-colors flex items-center justify-center">
                      <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" />
                    </div>
                  </div>

                  {/* Filename and Size */}
                  <div className="mt-1.5 flex items-center justify-between gap-1 text-[10px]">
                    <span className="font-medium text-slate-700 truncate max-w-[120px]" title={file.name}>
                      {file.name}
                    </span>
                    <span className="text-slate-400 shrink-0">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(idx);
                    }}
                    className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-slate-900/80 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow-sm"
                    title="Remove attachment"
                    aria-label="Remove attachment"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lightbox Preview Modal */}
      {activePreview && (
        <Dialog open={Boolean(activePreview)} onOpenChange={(open) => !open && setActivePreview(null)}>
          <DialogContent className="max-w-4xl p-2 bg-slate-950 border-slate-800">
            <DialogHeader className="p-2 border-b border-slate-800 text-white flex flex-row items-center justify-between">
              <DialogTitle className="text-sm font-semibold truncate text-slate-200">
                {activePreview.name}
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[75vh] overflow-auto flex items-center justify-center p-2">
              <img
                src={activePreview.url}
                alt={activePreview.name}
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
